import React, { useState, useRef, useEffect } from 'react';
import { api } from '../config/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  route?: string;
  confidence?: number;
  sources?: { file: string; page: string }[];
}

interface Stats {
  properties: number;
  active_leases: number;
  total_monthly_rent: number;
  documents: number;
  rag_chunks: number;
}

const EXAMPLE_QUESTIONS = [
  "What's the rent for Lewis?",
  "Which tenants are responsible for landscaping?",
  "What's the total monthly rent across all properties?",
  "When does the DiBuono lease expire?",
  "What's the pet policy?",
  "What are the emergency maintenance procedures?",
  "Show me all active leases",
  "Which properties have zero security deposit?",
];

export const LeaseQA: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [serviceUp, setServiceUp] = useState<boolean | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get('/lease-qa/stats')
      .then(res => { setStats(res.data); setServiceUp(true); })
      .catch(() => setServiceUp(false));
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const askQuestion = async (question: string) => {
    if (!question.trim() || isLoading) return;

    const userMsg: Message = { role: 'user', content: question };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const res = await api.post('/lease-qa/ask', { question });
      const assistantMsg: Message = {
        role: 'assistant',
        content: res.data.answer,
        route: res.data.route,
        confidence: res.data.confidence,
        sources: res.data.sources,
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err: any) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: err.response?.data?.detail || 'Failed to get answer. Is the Lease Q&A service running?',
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    askQuestion(input);
  };

  const clearChat = () => {
    setMessages([]);
    api.post('/lease-qa/clear-history').catch(() => {});
  };

  const routeBadgeColor = (route?: string) => {
    switch (route) {
      case 'structured': return 'bg-green-900/50 text-green-400 border-green-700/50';
      case 'rag': return 'bg-blue-900/50 text-blue-400 border-blue-700/50';
      case 'hybrid': return 'bg-yellow-900/50 text-yellow-400 border-yellow-700/50';
      default: return 'bg-gray-800 text-gray-400 border-gray-700';
    }
  };

  return (
    <div className="h-[calc(100vh-2rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono flex items-center gap-3">
            <span className="text-cyan-400">~/</span>lease-qa
            <span className="text-gray-600 text-sm font-normal">
              {stats ? `${stats.active_leases} leases \u00b7 ${stats.properties} properties \u00b7 $${stats.total_monthly_rent.toLocaleString()}/mo` : ''}
            </span>
          </h1>
          <p className="text-gray-500 text-sm mt-1 font-mono">
            Ask questions about your active leases &mdash; powered by AI
          </p>
        </div>
        <div className="flex gap-2">
          {serviceUp === false && (
            <span className="text-red-400 text-xs font-mono bg-red-900/30 px-3 py-1.5 rounded border border-red-800/50">
              Service Offline
            </span>
          )}
          <button
            onClick={clearChat}
            className="text-gray-500 hover:text-gray-300 text-xs font-mono bg-gray-800/50 px-3 py-1.5 rounded border border-gray-700/50 hover:border-gray-600 transition-colors"
          >
            Clear Chat
          </button>
        </div>
      </div>

      {/* Chat Area */}
      <div
        ref={chatRef}
        className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2"
        style={{ scrollBehavior: 'smooth' }}
      >
        {messages.length === 0 && (
          <div className="py-8">
            <p className="text-gray-500 font-mono text-sm mb-4">Try asking:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {EXAMPLE_QUESTIONS.map((q, i) => (
                <button
                  key={i}
                  onClick={() => askQuestion(q)}
                  className="text-left text-sm font-mono text-gray-400 bg-gray-800/40 hover:bg-gray-800/70 px-4 py-3 rounded-lg border border-gray-700/30 hover:border-cyan-800/50 transition-all hover:text-gray-200"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${
              msg.role === 'user'
                ? 'bg-cyan-900/30 border border-cyan-700/30 text-gray-200'
                : 'bg-gray-800/60 border border-gray-700/40 text-gray-300'
            } rounded-lg px-4 py-3`}>
              {msg.role === 'user' ? (
                <p className="font-mono text-sm">{msg.content}</p>
              ) : (
                <>
                  <div
                    className="font-mono text-sm leading-relaxed whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: msg.content
                        .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
                        .replace(/\n/g, '<br/>')
                        .replace(/\$(\d[\d,]*\.?\d*)/g, '<span class="text-green-400 font-semibold">$$$1</span>')
                    }}
                  />
                  {(msg.route || (msg.sources && msg.sources.length > 0)) && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-700/30">
                      {msg.route && (
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${routeBadgeColor(msg.route)}`}>
                          {msg.route}
                        </span>
                      )}
                      {msg.sources && msg.sources.length > 0 && (
                        <span className="text-gray-600 text-[10px] font-mono">
                          Sources: {msg.sources.map(s => s.file.split('_')[0]).join(', ')}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-800/60 border border-gray-700/40 rounded-lg px-4 py-3 flex items-center gap-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="font-mono text-sm text-cyan-400/70 animate-pulse">
                Searching lease documents...
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex gap-3 flex-shrink-0">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Ask about your leases..."
          autoFocus
          className="flex-1 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 py-3 text-gray-200 font-mono text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-700/50 transition-colors"
        />
        <button
          type="submit"
          disabled={isLoading || !input.trim()}
          className="bg-cyan-900/50 hover:bg-cyan-800/50 border border-cyan-700/50 text-cyan-400 font-mono text-sm font-semibold px-6 py-3 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Thinking...' : 'Ask'}
        </button>
      </form>
    </div>
  );
};

export default LeaseQA;
